'use client'

import { useState } from 'react'
import Modal from 'react-modal'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import { AudioPlayer as InlineAudioPlayer } from 'src/components/AudioPlayer'
import { VideoPlayer as InlineVideoPlayer } from 'src/components/VideoPlayer'
import { VideoPoster } from 'src/components/VideoPoster'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'
import TextContent from 'src/components/blocks/types/TextContent'
import Typography from 'src/components/design-system/Typography'
import DOMPurify from 'src/_helpers/dompurify'
import TranscriptionIcon from 'public/icons/interface/transcription.svg'
import AudioDescIcon from 'public/icons/interface/audiodesc.svg'
import PjmIcon from 'public/icons/interface/pjm.svg'
import { getLocalizedThumb } from 'src/_helpers/getLocalizedThumb'

export default function VideoPlayer({ block, locale }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [activeModal, setActiveModal] = useState(null)
  const translateEntry = useTranslateEntry()
  const translation = block.translations?.[locale] || {}
  const thumb = getLocalizedThumb(block, 'video_mask', locale)
  const thumbUrl = thumb ? getMediaUrl(thumb.url, thumb.version) : null
  const youtubePoster = block?.videoYoutube
    ? `https://img.youtube.com/vi/${block.videoYoutube}/maxresdefault.jpg`
    : null
  const posterUrl = thumbUrl || youtubePoster
  const alt = translation?.alt?.trim() || translation?.title?.trim() || ''

  const videoSrc = block?.videoYoutube
    ? `https://www.youtube.com/watch?v=${block.videoYoutube}`
    : block?.videoFile?.url
      ? getMediaUrl(block.videoFile.url)
      : null
  const audioDescriptionUrl = block?.audioDescriptionFile?.url ? getMediaUrl(block.audioDescriptionFile.url) : null
  const signLanguageSrc = block?.pslVideoYoutube
    ? `https://www.youtube.com/watch?v=${block.pslVideoYoutube}`
    : block?.pslFile?.url
      ? getMediaUrl(block.pslFile.url)
      : null

  const transcription = translation?.transcription ? DOMPurify.sanitize(translation.transcription) : null

  const featureBadges = [
    transcription && {
      key: 'transcription',
      label: translateEntry('T_TRANSCRIPTION'),
      icon: TranscriptionIcon,
      onClick: () => setActiveModal('transcription'),
      ariaLabel: translateEntry('T_OPEN_TRANSCRIPTION'),
    },
    audioDescriptionUrl && {
      key: 'audioDescription',
      label: translateEntry('T_AUDIOESCRIPTION'),
      icon: AudioDescIcon,
      onClick: () => setActiveModal('audio'),
      ariaLabel: translateEntry('T_OPEN_AUDIO_DESCRIPTION'),
    },
    signLanguageSrc && {
      key: 'signLanguage',
      label: translateEntry('T_SIGN_LANGUAGE'),
      icon: PjmIcon,
      onClick: () => setActiveModal('sign'),
      ariaLabel: translateEntry('T_OPEN_SIGN_LANGUAGE'),
    },
  ].filter(Boolean)

  const handlePlay = () => {
    if (!videoSrc) return
    setIsPlaying(true)
  }

  return (
    <section className={`bg-basic-gray10 ${block.type.value}`} id={`${block.uuid}`}>
      <div className="flex flex-col gap-24 py-64 d:px-40 px-16">
        <TextContent block={block} locale={locale} />

        <div className="relative w-full overflow-hidden rounded-2 min-h-190 d:min-h-665">
          {isPlaying && videoSrc ? (
            <InlineVideoPlayer src={videoSrc} title={translation?.title} autoplay />
          ) : (
            <VideoPoster
              src={posterUrl}
              alt={alt}
              width={thumb?.width || 1359}
              height={thumb?.height || 640}
              imageClassName="h-full min-h-[inherit] w-full object-cover aspect-[16/7.5]"
              onPlay={videoSrc ? handlePlay : null}
            />
          )}
        </div>

        {featureBadges.length > 0 && (
          <div className="flex flex-wrap gap-16">
            {featureBadges.map((feature) => (
              <button
                key={feature.key}
                onClick={feature.onClick}
                className="inline-flex cursor-pointer items-center gap-6 rounded bg-basic-white px-10 py-4 text-left shadow-[0_4px_14px_rgba(0,0,0,0.08)]"
                aria-haspopup="dialog"
                aria-label={feature.ariaLabel}
              >
                <feature.icon aria-hidden="true" />
                <Typography variant="button-l-bold" color="text-dark-blue-dark-blue2" className="max-t:hidden">
                  {feature.label}
                </Typography>
              </button>
            ))}
          </div>
        )}
      </div>

      {[
        {
          key: 'transcription',
          isOpen: activeModal === 'transcription',
          onClose: () => setActiveModal(null),
          contentLabel: translateEntry('T_TRANSCRIPTION_MODAL', true),
          render: () =>
            transcription && (
              <div
                className="text-block-content body-b2 text-dark-blue-dark-blue1"
                dangerouslySetInnerHTML={{ __html: transcription }}
              />
            ),
        },
        {
          key: 'audio',
          isOpen: activeModal === 'audio',
          onClose: () => setActiveModal(null),
          contentLabel: translateEntry('T_AUDIO_DESC_MODAL', true),
          render: () =>
            audioDescriptionUrl && (
              <div className="w-full max-w-4xl overflow-hidden rounded-1 bg-style3 shadow-[0_4px_14px_rgba(0,0,0,0.08)]">
                <InlineAudioPlayer
                  audio={audioDescriptionUrl}
                  title={translateEntry('T_AUDIOESCRIPTION')}
                  className="max-w-none"
                  style={{
                    '--audio-bg': 'transparent',
                    '--audio-border': 'none',
                    '--audio-filter': 'none',
                    '--audio-controls-color': 'var(--c-dark-blue1)',
                    '--audio-slider-track-bg': 'var(--c-dark-blue6)',
                    '--audio-slider-progress-bg': 'var(--c-dark-blue1)',
                    '--audio-button-size': '48px',
                    '--media-focus-ring-color': 'var(--c-focus-color)',
                  }}
                />
              </div>
            ),
        },
        {
          key: 'sign',
          isOpen: activeModal === 'sign',
          onClose: () => setActiveModal(null),
          contentLabel: translateEntry('T_PJM_MODAL', true),
          render: () =>
            signLanguageSrc && <InlineVideoPlayer src={signLanguageSrc} title={translation?.title} autoplay />,
        },
      ].map(
        (modal) =>
          modal.render && (
            <Modal
              key={modal.key}
              isOpen={modal.isOpen}
              onRequestClose={modal.onClose}
              contentLabel={modal.contentLabel}
              className="fixed left-1/2 top-1/2 w-[min(95vw,960px)] -translate-x-1/2 -translate-y-1/2 bg-basic-white p-24 outline-none"
              overlayClassName="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
            >
              {modal.render()}
            </Modal>
          ),
      )}
    </section>
  )
}
